import React, { useEffect, useState } from 'react'
import { Button } from '@mui/material'
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { CCol, CForm, CFormCheck, CFormInput, CFormLabel, CInputGroup, CInputGroupText, CRow } from '@coreui/react';
import { useAuth } from '../contexts/AuthContext';
import { useDiscount } from '../contexts/DiscountContext';
import CInput from './CInput';
import CSelect from './CSelect';
import CategorySelection from './CategorySelection';
import ProductSelection from './ProductSelection';

const billTypes = [
    { value: "bill", label: "Bill" },
    { value: "item", label: "Item" }
]

const itemTypes = [
    { value: "category", label: "Category" },
    { value: "product", label: "Product" },
]

const AddDiscount = ({ discountDetail, editMode, editDiscountId, setEditDiscountId, setEditMode, visible, setVisible, setDiscountDetail, fetchDiscount, canCreate, canUpdate, canDelete }) => {
    const { createDiscount, getSingleDiscount, deleteDiscount, updateDiscount } = useDiscount()
    const { restaurantId } = useAuth()

    const [categoryValidated, setCategoryValidated] = useState(false)
    const [productsValidated, setProductsValidated] = useState(false)
    const [validated, setValidated] = useState(false);

    useEffect(() => {
        fetchSingleDiscount();
    }, [editMode, editDiscountId]);

    const fetchSingleDiscount = async () => {
        if (editDiscountId) {
            const discount = await getSingleDiscount(editDiscountId)
            setDiscountDetail({ ...discount, type: discount.product_type });
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((editMode && !canUpdate) || (!editMode && !canCreate)) return;

        const form = e.currentTarget;
        setValidated(true);

        if (discountDetail.bill_type && discountDetail.bill_type === 'item') {
            if (discountDetail.item_type === 'category' && !discountDetail.category_id) {
                setCategoryValidated(true);
            } else {
                setCategoryValidated(false);
            }

            if (discountDetail.item_type === 'product' && discountDetail.products.length === 0) {
                setProductsValidated(true);
            } else {
                setProductsValidated(false);
            }
        }

        if (form.checkValidity() === false) {
            e.stopPropagation();

            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        try {
            const selectedProducts = discountDetail.products.length > 0 ? discountDetail.products.map(product => product.value) : [];
            const selectedCategory = discountDetail.category_id ? discountDetail.category_id.value : null;

            if (visible && restaurantId) {
                const data = await createDiscount({
                    ...discountDetail,
                    restaurant_id: restaurantId,
                    products: selectedProducts,
                    category_id: selectedCategory
                });

                if (!data.error) {
                    fetchDiscount(data.discountId);
                    handleClose();
                }
            } else if (editMode && editDiscountId && restaurantId) {
                const data = await updateDiscount(editDiscountId, {
                    ...discountDetail,
                    restaurant_id: restaurantId,
                    products: selectedProducts,
                    category_id: selectedCategory
                });

                if (!data.error) {
                    fetchDiscount();
                    handleClose();
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleClose = () => {
        setVisible(false);
        setEditMode(false);
        setEditDiscountId(null);
        setDiscountDetail({
            name: '',
            bill_type: "",
            percentage: "",
            item_type: "category",
            is_active: 1,
            type: "all",
            category_id: "",
            products: [],
            restaurant_id: restaurantId
        });
    };

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this discount?',
                header: 'Archive Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                await deleteDiscount(editDiscountId);
                handleClose();
                fetchDiscount()
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    }

    return (
        <>
            <ConfirmDialog />
            <Dialog draggable={false} header={`${visible ? 'Add Discount' : 'Edit Discount'}`} visible={visible || (editMode && editDiscountId !== null)} style={{ width: `35vw` }} onHide={handleClose}>
                <CForm onSubmit={handleSubmit} noValidate validated={validated}>
                    <div className='fields-container'>
                        <CRow className="mb-3 mt-3">
                            <CInput
                                label="Name"
                                type="text"
                                className='d-flex'
                                labelClassName='w-50'
                                value={discountDetail.name}
                                onChange={(value) => setDiscountDetail({ ...discountDetail, name: value })}
                                required={true}
                                errorMessage='Name is required.'
                            />
                        </CRow>
                        <CRow className="mb-3">
                            <CSelect
                                label="Bill Type"
                                className='d-flex'
                                labelClassName='w-50'
                                options={billTypes}
                                value={discountDetail.bill_type}
                                onChange={(value) => setDiscountDetail({ ...discountDetail, bill_type: value })}
                                required={true}
                                errorMessage='Discount bill type is required.'
                            />
                        </CRow>
                        {
                            discountDetail.bill_type && discountDetail.bill_type === 'item' && (
                                <CRow className="mb-3">
                                    <CSelect
                                        label="Item Type"
                                        className='d-flex'
                                        labelClassName='w-50'
                                        options={itemTypes}
                                        value={discountDetail.item_type}
                                        onChange={(value) => setDiscountDetail({ ...discountDetail, item_type: value })}
                                        required={discountDetail.bill_type && discountDetail.bill_type === 'item'}
                                        errorMessage='Discount item type is required.'
                                    />
                                </CRow>
                            )
                        }
                        <CRow className="mb-3">
                            <div className='d-flex'>
                                <div className='w-50'>
                                    <CFormLabel className="form-label">Percentage <span className="text-danger">*</span> </CFormLabel>
                                </div>
                                <div className='w-100'>
                                    <CInputGroup>
                                        <CInputGroupText>%</CInputGroupText>
                                        <CFormInput
                                            type='number'
                                            step='any'
                                            min="0"
                                            max="100"
                                            value={discountDetail.percentage}
                                            onChange={(e) => {
                                                let newValue = parseFloat(e.target.value);

                                                if (isNaN(newValue)) {
                                                    setDiscountDetail({ ...discountDetail, percentage: '' });
                                                    return;
                                                }

                                                if (newValue < 0) newValue = 0;
                                                if (newValue > 100) newValue = 100;

                                                setDiscountDetail({ ...discountDetail, percentage: newValue });
                                            }}
                                            required
                                            feedbackInvalid='Please enter valid discount percentage.'
                                        />
                                    </CInputGroup>
                                </div>
                            </div>
                        </CRow>
                        {
                            discountDetail?.bill_type === 'item' && discountDetail.item_type && discountDetail.item_type !== 'product' && (
                                <CRow className="mb-3">
                                    <CategorySelection
                                        className='d-flex'
                                        labelClassName='w-50'
                                        value={discountDetail.category_id}
                                        onChange={(value) => setDiscountDetail({ ...discountDetail, category_id: value })}
                                        multiSelect={false}
                                        required={discountDetail.item_type && discountDetail.item_type === 'category'}
                                        error={categoryValidated}
                                    />
                                </CRow>
                            )
                        }
                        {
                            discountDetail?.bill_type === 'item' && discountDetail.item_type && discountDetail.item_type === 'product' && (
                                <>
                                    <CRow className="mb-3">
                                        <div className='d-flex'>
                                            <div className='w-50'>
                                                <CFormLabel>Filter By</CFormLabel>
                                            </div>
                                            <div className='d-flex w-100'>
                                                <CFormCheck
                                                    className='me-4'
                                                    type="radio"
                                                    name="sizeOptions"
                                                    id="sizeRegular"
                                                    value="regular"
                                                    checked={discountDetail.type === 'all'}
                                                    onChange={() => setDiscountDetail({ ...discountDetail, type: 'all' })}
                                                    label="All"
                                                />
                                                <CFormCheck
                                                    className='me-4'
                                                    type="radio"
                                                    name="sizeOptions"
                                                    id="sizeSmall"
                                                    value="small"
                                                    checked={discountDetail.type === 'food'}
                                                    onChange={() => setDiscountDetail({ ...discountDetail, type: 'food' })}
                                                    label="Food"
                                                />
                                                <CFormCheck
                                                    className='me-4'
                                                    type="radio"
                                                    name="sizeOptions"
                                                    id="sizeRegular"
                                                    value="regular"
                                                    checked={discountDetail.type === 'beverage'}
                                                    onChange={() => setDiscountDetail({ ...discountDetail, type: 'beverage' })}
                                                    label="Beverage"
                                                />
                                            </div>
                                        </div>
                                    </CRow>
                                    <CRow className='mb-3'>
                                        <ProductSelection
                                            className='d-flex'
                                            labelClassName='w-50'
                                            multiSelect={true}
                                            type={discountDetail.type}
                                            value={discountDetail.products}
                                            onChange={(value) => setDiscountDetail({ ...discountDetail, products: value })}
                                            required={discountDetail.item_type && discountDetail.item_type === 'product'}
                                            error={productsValidated}
                                        />
                                    </CRow>
                                </>
                            )
                        }
                        <CRow className='mb-3'>
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Active</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='px-1'>
                                <Checkbox
                                    checked={discountDetail.is_active === 1}
                                    onChange={(event) => setDiscountDetail({ ...discountDetail, is_active: event.target.checked ? 1 : 0 })}
                                />
                            </CCol>
                        </CRow>
                    </div>
                    <div className={`d-flex button-container ${(editMode && editDiscountId && canDelete) ? 'justify-content-between' : 'justify-content-end'}`}>
                        {
                            (editMode && editDiscountId && canDelete) && (
                                <div>
                                    <Button color='error' variant="contained" onClick={handleDelete} >
                                        Delete
                                    </Button>
                                </div>
                            )
                        }
                        <div className='d-flex'>
                            <Button onClick={handleClose} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            {((!editMode && canCreate) || (editMode && canUpdate)) && (
                                <Button fullWidth variant="contained" color="primary" type="submit" className="ms-3">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </div>
                </CForm>
            </Dialog>
        </>
    )
}

export default AddDiscount
